import { Component, Input, OnChanges, ChangeDetectionStrategy, output } from '@angular/core';
import { EPSON_PAGES_ALL, EPSON_PAGES_DEFAULT, formatEpsonPages, parseEpsonPages } from 'src/app/models/epson.model';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { NgClass } from '@angular/common';
import { MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { TranslatePipe } from '@ngx-translate/core';

type PagesMode = 'first' | 'all' | 'custom';

/**
 * Choix des pages à garder : première page (défaut du kiosque), toutes, ou
 * sélection libre. Quand le nombre de pages du document est connu, la sélection
 * se fait en cliquant sur les pages ; sinon on retombe sur la saisie « 1-3, 5 ».
 */
@Component({
    selector: 'app-epson-pages-select',
    template: `
        <section class="flex flex-col gap-2">
          <span class="font-bold text-sm">
            {{ 'EPSON.PAGES.LABEL' | translate }}
            @if (numberOfPages) {
              <span class="font-normal text-tw-text-disabled">
                · {{ 'EPSON.PAGES.DOCUMENT' | translate: { count: numberOfPages } }}
              </span>
            }
          </span>
          <mat-button-toggle-group [value]="mode" (change)="setMode($event.value)" class="w-full">
            <mat-button-toggle value="first" class="flex-1">{{ 'EPSON.PAGES.FIRST' | translate }}</mat-button-toggle>
            <mat-button-toggle value="all" class="flex-1">{{ 'EPSON.PAGES.ALL' | translate }}</mat-button-toggle>
            <mat-button-toggle value="custom" class="flex-1">{{ 'EPSON.PAGES.CUSTOM' | translate }}</mat-button-toggle>
          </mat-button-toggle-group>
        
          @if (mode === 'custom') {
            @if (numberOfPages) {
              <span class="text-xs text-tw-text-disabled">{{ 'EPSON.PAGES.CLICK_HINT' | translate }}</span>
              <div class="grid grid-cols-8 gap-1 max-h-40 overflow-y-auto">
                @for (page of pageNumbers; track page) {
                  <button type="button"
                    class="h-8 rounded border text-sm"
                    [ngClass]="isSelected(page) ? 'bg-tw-primary text-tw-text-light border-transparent font-bold' : 'bg-white border-gray-300 hover:bg-tw-primary hover:bg-opacity-10'"
                  (click)="togglePage(page)">{{ page }}</button>
                }
              </div>
              <span class="text-xs" [ngClass]="error ? 'text-tw-error' : 'text-tw-text-disabled'">
                {{ error ? (error | translate: { count: numberOfPages }) : ('EPSON.PAGES.SELECTED' | translate: { pages: custom }) }}
              </span>
            } @else {
              <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-full">
                <mat-label>{{ 'EPSON.PAGES.INPUT' | translate }}</mat-label>
                <input matInput [ngModel]="custom" (ngModelChange)="setCustom($event)" placeholder="1-3, 5">
                @if (!error) {
                  <mat-hint>{{ 'EPSON.PAGES.HINT' | translate }}</mat-hint>
                }
                @if (error) {
                  <mat-hint class="!text-tw-error">{{ error | translate: { count: numberOfPages } }}</mat-hint>
                }
              </mat-form-field>
            }
          }
        </section>
        `,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatButtonToggleGroup, MatButtonToggle, NgClass, MatFormField, MatLabel, MatInput, FormsModule, RxReactiveFormsModule, MatHint, TranslatePipe]
})
export class EpsonPagesSelectComponent implements OnChanges {

    /** Sélection au format « 1-3, 5 » ; vide pour toutes. */
    @Input() pages: string = EPSON_PAGES_DEFAULT;
    readonly pagesChange = output<string>();
    /** false quand la saisie ne peut pas être envoyée. */
    readonly validChange = output<boolean>();
    @Input() numberOfPages?: number;

    mode: PagesMode = 'first';
    custom = '';
    error = '';
    pageNumbers: number[] = [];
    protected selected = new Set<number>();

    ngOnChanges(): void {
        this.pageNumbers = Array.from({ length: this.numberOfPages || 0 }, (_, i) => i + 1);
        if (this.mode !== 'custom') {
            this.mode = this.pages === EPSON_PAGES_ALL ? 'all' : this.pages === EPSON_PAGES_DEFAULT ? 'first' : 'custom';
            if (this.mode === 'custom') {
                // Valeur reçue du parent : on l'affiche sans la lui renvoyer.
                this.custom = this.pages;
                this.selected = new Set(parseEpsonPages(this.pages).pages || []);
            }
        }
        this.validate();
    }

    setMode(mode: PagesMode): void {
        this.mode = mode;
        if (mode === 'custom') {
            // On part de la première page, comme le défaut du kiosque.
            this.setCustom(this.custom || EPSON_PAGES_DEFAULT);
            return;
        }
        this.emit(mode === 'first' ? EPSON_PAGES_DEFAULT : EPSON_PAGES_ALL);
    }

    isSelected(page: number): boolean {
        return this.selected.has(page);
    }

    togglePage(page: number): void {
        if (this.selected.has(page)) {
            this.selected.delete(page);
        } else {
            this.selected.add(page);
        }
        this.custom = formatEpsonPages([...this.selected]);
        this.emit(this.custom);
    }

    setCustom(value: string): void {
        this.custom = value;
        this.selected = new Set(parseEpsonPages(value).pages || []);
        this.emit(value);
    }

    protected emit(value: string): void {
        this.pages = value;
        this.pagesChange.emit(value);
        this.validate();
    }

    protected validate(): void {
        const { error } = parseEpsonPages(this.pages, this.numberOfPages);
        const empty = this.mode === 'custom' && !this.custom.trim();
        this.error = empty ? 'EPSON.PAGES.EMPTY' : error ? (error.startsWith('>') ? 'EPSON.PAGES.TOO_FAR' : 'EPSON.PAGES.INVALID') : '';
        this.validChange.emit(!this.error);
    }
}
